import { Button, makeStyles } from '@material-ui/core';
import _, {
  isEmpty, map, reject, find, forEach, sortBy, groupBy,
} from 'lodash';
import moment from 'moment';
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import config from '@/configs/config';
import ConfirmModal from './ConfirmModal';
import Header from './Header';
import Item, { StyledCheckbox } from './Item';
import { useShoppingCartContext } from '../../contexts/shoppingCart';

export const genGroupId = (item) => {
  const {
    key,
    specs,
    provider,
  } = item;
  return `${provider}_${key}_${JSON.stringify(specs)}`;
};

const useStyles = makeStyles((theme) => ({
  root: {
    paddingBottom: theme.spacing(9),
    minHeight: 'calc(100vh - 280px)',
    textAlign: 'left',
    width: '100%',
  },
  header: {
    alignItems: 'stretch',
    display: 'none',
    flexDirection: 'column',
    marginTop: theme.spacing(2),

    [theme.breakpoints.up('sm')]: { display: 'flex' },
  },
  body: { [theme.breakpoints.up('sm')]: { marginTop: theme.spacing(3) } },
  footer: {
    alignItems: 'stretch',
    background: '#f9f9f9',
    bottom: 0,
    display: 'flex',
    height: 60,
    position: 'fixed',
    width: '100%',
    zIndex: 20,

    [theme.breakpoints.up('sm')]: {
      position: 'static',
      background: '#f2f2f2',
      marginTop: theme.spacing(2),
      padding: theme.spacing(1, 1.5),
      justifyContent: 'end',
    },
  },
  checkboxContainer: {
    alignSelf: 'center',

    [theme.breakpoints.up('sm')]: { display: 'none' },
  },
  checkoutButton: {
    background: config.themeBackgroundColor,
    borderRadius: 5,
    marginLeft: theme.spacing(3),
    color: 'white',
  },
  priceContainer: {
    alignSelf: 'center',
    flexGrow: 1,
    fontWeight: 'bold',
    textAlign: 'right',

    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      alignItems: 'center',
      flexGrow: 0,

      '& > div + div': { marginLeft: theme.spacing(1) },
    },
  },
}));

export default () => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();

  const { shoppingCart, removeItem, addItem } = useShoppingCartContext();

  const onAddButtonClick = useCallback((item) => {
    addItem({
      ...item,
    });
  }, [addItem]);

  const data = useMemo(() => sortBy(
    map(
      groupBy(shoppingCart, genGroupId),
      (value, id) => {
        const [item] = value || [];
        const { latestRegisterTime } = item || [];
        const disabled = moment().isAfter(latestRegisterTime);
        return {
          id,
          data: item,
          disabled,
          amount: value.length,
          items: value,
        };
      }
    ),
    'id'
  ), [shoppingCart]);

  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState([]);
  const totalPrice = useMemo(() => {
    let total = 0;

    selectedItemIds.forEach((id) => {
      const item = data.find(({ id: itemId }) => id === itemId);
      const { amount, data: itemData } = item || {};
      const { price, sellPrice } = itemData || {};

      total += (sellPrice || price || 0) * (amount || 1);
    });

    return total;
  }, [selectedItemIds, data]);
  const isSelectAllChecked = useMemo(
    () => {
      const dataLen = (data || []).filter(({ disabled }) => !disabled).length;
      if (dataLen === 0) return false;
      return selectedItemIds.length === dataLen;
    },
    [selectedItemIds, data]
  );

  const onCheckoutButtonClick = () => {
    if (isEmpty(selectedItemIds)) return;

    const dataToConfirm = [];

    forEach(selectedItemIds, (id) => {
      const item = find(data, ['id', id]);
      dataToConfirm.push(item);
    });

    setConfirmModalData(dataToConfirm);
    setIsConfirmModalOpen(true);
  };

  const onConfirmModalOkButtonClick = () => {
    if (isEmpty(selectedItemIds)) return;
    history.push('/ec/checkout', { itemIds: selectedItemIds });
  };

  useEffect(
    () => {
      const allItems = reject((data || []), 'disabled');
      const defaultProvider = _.get(allItems, [0, 'data', 'provider'], '');
      setSelectedItemIds(allItems.filter((d) => _.get(d, ['data', 'provider'], '') === defaultProvider).map((d) => d.id));
    },
    [data]
  );

  const onConfirmModalCloseButtonClick = () => {
    setIsConfirmModalOpen(false);
    setConfirmModalData([]);
  };

  const onCheckboxChange = (id) => (newValue) => {
    if (newValue) setSelectedItemIds([...selectedItemIds, id]);
    else setSelectedItemIds(selectedItemIds.filter((selectedItemId) => selectedItemId !== id));
  };

  const onSelectAllCheckboxChange = (_e, newValue) => {
    const allItemId = (data || []).filter(({ disabled }) => !disabled).map(({ id }) => id);
    if (newValue) setSelectedItemIds(allItemId);
    else setSelectedItemIds([]);
  };

  const onRemoveButtonClick = (id) => () => {
    const itemsToRemove = data.find((({ id: itemId }) => itemId === id)).items;
    removeItem(itemsToRemove);
    setSelectedItemIds(selectedItemIds.filter((selectedItemId) => id !== selectedItemId));
  };

  const onGoBackClick = () => {
    history.go(-1);
  };

  if (isEmpty(data)) {
    return (
      <div
        className={classes.root}
        style={{
          alignItems: 'start',
          display: 'flex',
          justifyContent: 'center',
          paddingTop: 24,
        }}
      >
        {t('ec.empty_cart_hint')}
        <Link to="/">{t('ec.go_back_and_shop')}</Link>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={onConfirmModalCloseButtonClick}
        onOkbuttonClick={onConfirmModalOkButtonClick}
        data={confirmModalData}
      />
      <div className={classes.header}>
        <Header
          onSelectAllCheckboxChange={onSelectAllCheckboxChange}
          isSelectAllChecked={isSelectAllChecked}
          onGoBackClick={onGoBackClick}
        />
      </div>
      <div className={classes.body}>
        {(data || []).map((item) => {
          const {
            id, data: itemData, amount, disabled,
          } = item || {};
          return (
            <Item
              amount={amount || 0}
              checked={selectedItemIds.includes(id)}
              data={itemData || {}}
              disabled={disabled}
              key={id}
              onAddButtonClick={() => { onAddButtonClick(itemData); }}
              onCheckboxChange={onCheckboxChange(id)}
              onRemoveButtonClick={onRemoveButtonClick(id)}
              onSubButtonClick={() => { removeItem(itemData); }}
            />
          );
        })}
      </div>
      <div className={classes.footer}>
        <div className={classes.checkboxContainer}>
          <StyledCheckbox checked={isSelectAllChecked} onChange={onSelectAllCheckboxChange} />
          {t('ec.select_all_from_cart')}
        </div>
        <div className={classes.priceContainer}>
          <div>
            {t('ec.total')}
          </div>
          <div style={{ color: '#a80000' }}>
            {`$${totalPrice.toLocaleString('en-US')}`}
          </div>
        </div>
        <Button
          className={classes.checkoutButton}
          variant="contained"
          disabled={selectedItemIds.length === 0}
          onClick={onCheckoutButtonClick}
        >
          {t('ec.checkout')}
        </Button>
      </div>
      <div />
    </div>
  );
};
